import React from 'react';
import { SITE_CONTENT } from '../constants';

const UpcomingConcerts: React.FC = () => {
  const { concerti } = SITE_CONTENT;
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [isFading, setIsFading] = React.useState(false);

  React.useEffect(() => {
    if (!concerti || concerti.length <= 1) return;

    const interval = setInterval(() => {
      handleNext();
    }, 5000);

    return () => clearInterval(interval);
  }, [activeIndex, concerti]);

  const handleNext = () => {
    setIsFading(true);
    setTimeout(() => {
      setActiveIndex((prev) => (prev + 1) % concerti.length);
      setIsFading(false);
    }, 500);
  };

  const renderEmptyState = () => (
    <section id="concerts-section" className="pt-10 pb-24 bg-midnight relative overflow-hidden scroll-mt-56">
      <div className="absolute top-0 right-0 w-96 h-96 bg-gold/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      <div className="max-w-4xl mx-auto px-6 relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-serif text-white mb-4 uppercase tracking-widest">
            Agenda Concerti
          </h2>
          <div className="w-16 h-px bg-gold mx-auto" />
        </div>
        <div className="relative bg-white/5 border border-gold/30 p-12 md:p-20 shadow-2xl backdrop-blur-sm overflow-hidden flex flex-col justify-center items-center text-center">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-gold/50 to-transparent" />
          <p className="text-xl md:text-3xl font-serif text-white/70 italic leading-relaxed">
            Pubblicheremo presto le nuove date dei concerti estivi
          </p>
          <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-gold/50 to-transparent" />
        </div>
      </div>
    </section>
  );

  if (!concerti || concerti.length === 0) return renderEmptyState();

  const currentConcert = concerti[activeIndex];

  return (
    <section id="concerts-section" className="pt-10 pb-24 bg-midnight relative overflow-hidden scroll-mt-56">
      {/* Background Decorative Element */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gold/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-gold/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
      
      <div className="max-w-4xl mx-auto px-6 relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-serif text-white mb-4 uppercase tracking-widest">
            Agenda Concerti
          </h2>
          <div className="w-16 h-px bg-gold mx-auto" />
        </div>

        {/* The Scrolling Box */}
        <div className="relative bg-white/5 border border-gold/30 p-10 md:p-16 shadow-2xl backdrop-blur-sm overflow-hidden min-h-[350px] flex flex-col justify-center">
          {/* Animated Accent */}
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-gold/50 to-transparent" />
          <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-gold/50 to-transparent" />

          <div className={`transition-all duration-500 transform ${isFading ? 'opacity-0 -translate-y-4' : 'opacity-100 translate-y-0'}`}>
            <div className="flex flex-col items-center text-center">
              <div className="mb-8">
                <span className="text-gold font-sans uppercase tracking-[0.3em] text-sm block mb-2">
                  {currentConcert.data}
                </span>
                <span className="text-white/50 text-xs uppercase tracking-widest">
                  Ore {currentConcert.ora}
                </span>
              </div>

              <h3 className="text-3xl md:text-5xl font-serif text-white mb-8 leading-tight">
                {currentConcert.titolo}
              </h3>

              <div className="flex flex-col items-center space-y-4">
                <div className="flex items-center text-gold-light/80">
                  <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="text-lg tracking-wide italic">
                    {currentConcert.luogo}
                  </span>
                </div>
                
                {currentConcert.nota && (
                  <span className="inline-block px-4 py-1 rounded-full border border-gold/20 text-gold text-[10px] uppercase tracking-widest bg-gold/5">
                    {currentConcert.nota}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Navigation Dots */}
          {concerti.length > 1 && (
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex space-x-3">
              {concerti.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setIsFading(true);
                    setTimeout(() => {
                      setActiveIndex(idx);
                      setIsFading(false);
                    }, 500);
                  }}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    activeIndex === idx ? 'bg-gold w-6' : 'bg-gold/20'
                  }`}
                  aria-label={`Vai al concerto ${idx + 1}`}
                />
              ))}
            </div>
          )}
        </div>

        <div className="mt-12 text-center">
          <p className="text-white/30 text-xs uppercase tracking-[0.4em]">
            Iscriviti alla nostra newsletter per rimanere aggiornato
          </p>
        </div>
      </div>
    </section>
  );
};

export default UpcomingConcerts;